<template>
  <InfuseTree :root-node="toNodes(componentGroup)" class="tree pr-4">
    <template #node="{ node, parentNode }">
      <summary
        class="flex flex-row justify-between"
        :class="isComponentGroup(node.data) ? 'bg-primary-200' : 'bg-primary-50'"
      >
        <span>{{ node.label() }}{{ node.data.instance && `: ${node.data.instance}` }}</span>
        <span v-if="!isComponentGroup(node.data) || onboarding" class="mr-2">
          <button
            data-testid="page-add-variable-button"
            class="flex h-full px-5 items-center justify-center gap-1.5 text-xs text-secondary-500 hover:text-secondary-300 active:text-secondary-600 disabled:text-secondary-100 select-none"
            :class="loading ? 'cursor-not-allowed' : ''"
            :disabled="loading"
            @click.stop="emit('createVariable', node.data, onboarding ? undefined : parentNode.data)"
          >
            <font-awesome-icon :icon="['fas', 'plus']" />
            <span>{{ t('cpms.configuration.tree.addVariable') }}</span>
          </button>
        </span>
      </summary>
    </template>

    <template #leaf="{ leaf }">
      <ConfigurationComponentTreeVariable
        :variable="leaf.data"
        :variable-attribute="findByType(leaf.data.attributes, AttributeType.ACTUAL)"
        :loading="loading"
        type="variable"
        @get-variable="emit('getVariable', leaf.data, AttributeType.ACTUAL)"
        @set-variable="emit('setVariable', leaf.data, AttributeType.ACTUAL)"
        @remove-variable="emit('removeVariable', leaf.data, AttributeType.ACTUAL)"
        :onboarding="onboarding"
      />

      <ul v-if="leaf.data.attributes.length > 1 || !findByType(leaf.data.attributes, AttributeType.ACTUAL)">
        <li
          v-for="(attr, index) in leaf.data.attributes.filter(
            (va: VariableAttribute) => va.type !== AttributeType.ACTUAL
          )"
          :key="`attr-${index}-${attr.type}`"
        >
          <ConfigurationComponentTreeVariable
            :variable="leaf.data"
            :variable-attribute="attr"
            :loading="loading"
            type="variableAttribute"
            @get-variable="emit('getVariable', leaf.data, attr.type)"
            @set-variable="emit('setVariable', leaf.data, attr.type)"
            @remove-variable="emit('removeVariable', leaf.data, attr.type)"
            :onboarding="onboarding"
          />
        </li>
      </ul>
    </template>
  </InfuseTree>
</template>

<script setup lang="ts">
import {
  AttributeType,
  Component,
  ComponentGroup,
  Variable,
  VariableAttribute
} from '@infuse/cpms/charging-station/store';
import { ConfigurationComponentTreeVariable } from '@infuse/cpms/charging-station/ui';
import { InfuseTree } from '@infuse/ui/components';
import { UiTreeNode } from '@infuse/ui/domain';
import { useI18n } from 'vue-i18n';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

interface Props {
  componentGroup: ComponentGroup;
  loading?: boolean;
  onboarding?: boolean;
}

withDefaults(defineProps<Props>(), {
  onboarding: false
});

const { t } = useI18n();

interface Emits {
  (e: 'setVariable', variable: Variable, attributeType: AttributeType): void;

  (e: 'getVariable', variable: Variable, attributeType: AttributeType): void;

  (e: 'createVariable', component: Component, componentGroup: ComponentGroup): void;

  (e: 'removeVariable', variable: Variable, attributeType: AttributeType): void;
}

const emit = defineEmits<Emits>();

function toNodes(value: ComponentGroup | Component): UiTreeNode<ComponentGroup | Component, Variable> {
  if (isComponentGroup(value)) {
    const group = value as ComponentGroup;
    return {
      key: `componentType.${group.type}.${group.evseId || -1}.${group.connectorId || -1}`,
      name: group.type,
      label: () =>
        t(`componentType.${group.type}`, {
          evseId: group.evseId?.toString() ?? '',
          connectorId: group.connectorId?.toString() ?? ''
        }),
      children: [...group.components, ...group.componentGroups].map(toNodes),
      data: value,
      leafs: []
    };
  } else {
    const component = value as Component;
    return {
      key: `component.${component.name}.${component.instance || '_none_'}`,
      name: component.name,
      label: () => component.name,
      children: [],
      data: component,
      leafs: component.variables.map((variable) => ({
        key: `variable.${variable.name}.${variable.instance || '_none_'}`,
        name: variable.name,
        data: variable
      }))
    };
  }
}

function isComponentGroup(value: ComponentGroup | Component): boolean {
  return value.hasOwnProperty('componentGroups');
}

function findByType(variableAttributes: VariableAttribute[], type: AttributeType): VariableAttribute {
  const attribute = variableAttributes.find((va) => va.type === type);
  if (!attribute) {
    throw new Error(`Could not find attribute of type ${type}`);
  }

  return attribute;
}
</script>

<style lang="scss">
// indents and borders
$border-style: dotted;
$border: theme('colors.neutral.200');
$border-hover: theme('colors.neutral.400');
$ident: 10px;
$left: -($ident);

.tree {
  ul {
    margin-left: $ident;
  }

  > li {
    margin-left: 0 !important;
  }

  > li:first-child:before {
    content: none;
  }

  > li:first-child:after {
    border-left: none;
  }

  ul:last-child {
    > li {
      &:last-child:after {
        border-left: none;
      }
    }
  }

  li {
    list-style-type: none;
    margin: 6px 0 6px 6px;
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: -6px;
      left: $left;
      border-left: 1px $border-style $border;
      border-bottom: 1px $border-style $border;
      width: $ident;
      height: 15px;
    }

    &:after {
      position: absolute;
      content: '';
      top: 5px;
      left: $left;
      border-left: 1px $border-style $border;
      width: $ident;
      height: 100%;
    }

    &:last-child:after {
      border-top: none;
    }

    div.summary,
    summary {
      display: flex;
      border: 1px solid $border;
      padding: 6px 8px 6px 8px;
      text-decoration: none;
      cursor: pointer;
      border-radius: 3px;

      &:hover {
        border: 1px solid $border-hover;
      }
    }

    div.summary.variable {
      display: block;
    }
  }
}
</style>

<i18n>
{
  "en": {
    "componentType": {
      "CHARGING_STATION": "Charging station",
      "CONNECTOR": "Connector {connectorId}",
      "EVSE": "Evse {evseId}"
    }
  },
  "nl": {
    "componentType": {
      "CHARGING_STATION": "Laadpaal",
      "CONNECTOR": "Aansluiting {connectorId}",
      "EVSE": "Evse {evseId}"
    }
  }
}
</i18n>
